import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    BookOpen,
    Download,
    Edit3,
    FileUp,
    Loader2,
    Mail,
    Plus,
    Save,
    Trash2,
    Upload,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";

const EMPTY = {
    id: null,
    slug: "",
    title: "",
    subtitle: "",
    meta: [
        { label: "For", value: "" },
        { label: "Markets", value: "NZ + AU" },
        { label: "Read time", value: "12 mins · 3 pages" },
    ],
    pages: [
        { heading: "Page 1 — ", intro: "", points: [""] },
        { heading: "Page 2 — ", intro: "", points: [""] },
        { heading: "Page 3 — ", intro: "", points: [""] },
    ],
    is_published: true,
    order: 0,
    price: 9,
    custom_pdf_path: null,
};

function slugify(t) {
    return (t || "")
        .toLowerCase()
        .trim()
        .replace(/['"]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

export default function AdminBooklets() {
    const { user, loading } = useAuth();
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loadingList, setLoadingList] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [testingEmail, setTestingEmail] = useState(null);
    const [testLinks, setTestLinks] = useState({});
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            nav("/admin/login");
            return;
        }
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, loading]);

    async function reload() {
        setLoadingList(true);
        try {
            const { data } = await api.get("/admin/booklets");
            setItems(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load");
        } finally {
            setLoadingList(false);
        }
    }

    async function save(e) {
        e?.preventDefault?.();
        if (!editing.title.trim()) {
            toast.error("Title is required.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                slug: editing.slug || slugify(editing.title),
                title: editing.title,
                subtitle: editing.subtitle,
                meta: editing.meta.filter((m) => m.label || m.value),
                pages: editing.pages
                    .map((p) => ({
                        heading: p.heading,
                        intro: p.intro,
                        points: (p.points || []).filter((x) => x && x.trim()),
                    }))
                    .filter((p) => p.heading || p.intro || p.points.length),
                price: Number(editing.price) || 9,
                is_published: editing.is_published,
                order: editing.order,
            };
            let saved;
            if (editing.id) {
                const r = await api.patch(`/admin/booklets/${editing.id}`, payload);
                saved = r.data;
                toast.success("Booklet updated");
            } else {
                const r = await api.post("/admin/booklets", payload);
                saved = r.data;
                toast.success("Booklet created");
            }
            setEditing({ ...saved });
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
        } finally {
            setSaving(false);
        }
    }

    async function del(id) {
        if (!window.confirm("Delete this booklet? Buyers with existing download links will lose access.")) return;
        try {
            await api.delete(`/admin/booklets/${id}`);
            toast.success("Deleted");
            if (editing?.id === id) setEditing(null);
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't delete");
        }
    }

    async function uploadPdf(file) {
        if (!editing?.id) {
            toast.error("Save the booklet first, then upload a PDF.");
            return;
        }
        if (!file) return;
        if (!/\.pdf$/i.test(file.name)) {
            toast.error("Please pick a .pdf file.");
            return;
        }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post(
                `/admin/booklets/${editing.id}/upload-pdf`,
                form,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setEditing({ ...data });
            toast.success("PDF uploaded — buyers now get your version");
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    }

    async function removePdf() {
        if (!editing?.id || !editing.custom_pdf_path) return;
        if (!window.confirm("Remove your uploaded PDF and go back to the auto-generated version?")) return;
        try {
            const { data } = await api.delete(`/admin/booklets/${editing.id}/upload-pdf`);
            setEditing({ ...data });
            toast.success("Back to auto-generated PDF");
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove");
        }
    }

    async function sendTestEmail(r) {
        setTestingEmail(r.id);
        try {
            const { data } = await api.post(`/admin/booklets/${r.id}/send-test-email`, {});
            setTestLinks((prev) => ({ ...prev, [r.id]: data.download_url }));
            toast.success(`Test email sent to ${data.to} — or use the "Open test PDF" link that just appeared`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send test email");
        } finally {
            setTestingEmail(null);
        }
    }

    function updatePage(idx, patch) {
        setEditing((e) => ({
            ...e,
            pages: e.pages.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
        }));
    }
    function updatePoint(pageIdx, ptIdx, val) {
        setEditing((e) => ({
            ...e,
            pages: e.pages.map((p, i) =>
                i === pageIdx
                    ? { ...p, points: p.points.map((x, j) => (j === ptIdx ? val : x)) }
                    : p,
            ),
        }));
    }
    function addPoint(pageIdx) {
        setEditing((e) => ({
            ...e,
            pages: e.pages.map((p, i) =>
                i === pageIdx ? { ...p, points: [...p.points, ""] } : p,
            ),
        }));
    }
    function removePoint(pageIdx, ptIdx) {
        setEditing((e) => ({
            ...e,
            pages: e.pages.map((p, i) =>
                i === pageIdx
                    ? { ...p, points: p.points.filter((_, j) => j !== ptIdx) }
                    : p,
            ),
        }));
    }
    function updateMeta(idx, patch) {
        setEditing((e) => ({
            ...e,
            meta: e.meta.map((m, i) => (i === idx ? { ...m, ...patch } : m)),
        }));
    }

    const inputCls =
        "w-full border-2 border-ink/20 bg-white px-4 py-2.5 text-ink focus:border-coral focus:outline-none";

    return (
        <div className="min-h-screen bg-page text-ink" data-testid="admin-booklets-page">
            <header className="border-b-2 border-ink/10 bg-white">
                <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-5 lg:px-10">
                    <div className="flex items-center gap-3">
                        <Link
                            to="/admin"
                            className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-ink/60 hover:text-coral"
                            data-testid="admin-booklets-back"
                        >
                            <ArrowLeft className="h-4 w-4" /> Dashboard
                        </Link>
                        <span className="text-ink/25">/</span>
                        <span className="font-display text-lg font-black uppercase tracking-tight text-ink">
                            Booklets
                        </span>
                    </div>
                    {!editing && (
                        <button
                            onClick={() => setEditing({ ...EMPTY, order: items.length })}
                            className="inline-flex items-center gap-2 border-2 border-coral bg-coral px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white hover:-translate-y-0.5 transition-all"
                            data-testid="admin-booklets-new"
                        >
                            <Plus className="h-4 w-4" /> New booklet
                        </button>
                    )}
                </div>
            </header>

            <main className="mx-auto max-w-[1100px] px-5 py-8 lg:px-10">
                {editing ? (
                    <form
                        onSubmit={save}
                        className="border-2 border-ink/15 bg-white p-6 lg:p-9"
                        data-testid="admin-booklets-form"
                    >
                        <div className="mb-6 flex items-center justify-between">
                            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                                {editing.id ? "Edit booklet" : "New booklet"}
                            </div>
                            <button
                                type="button"
                                onClick={() => setEditing(null)}
                                className="text-ink/45 hover:text-coral"
                                aria-label="Close"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="grid gap-5 md:grid-cols-2">
                            <Field label="Title">
                                <input
                                    value={editing.title}
                                    onChange={(ev) =>
                                        setEditing({
                                            ...editing,
                                            title: ev.target.value,
                                            slug: editing.id ? editing.slug : slugify(ev.target.value),
                                        })
                                    }
                                    className={inputCls}
                                    required
                                    data-testid="admin-booklets-title"
                                />
                            </Field>
                            <Field label="URL slug">
                                <input
                                    value={editing.slug}
                                    onChange={(ev) => setEditing({ ...editing, slug: ev.target.value })}
                                    className={`${inputCls} font-mono text-sm`}
                                    data-testid="admin-booklets-slug"
                                />
                            </Field>
                        </div>

                        <Field label="Subtitle (shown on the cover)">
                            <input
                                value={editing.subtitle}
                                onChange={(ev) => setEditing({ ...editing, subtitle: ev.target.value })}
                                className={inputCls}
                                data-testid="admin-booklets-subtitle"
                            />
                        </Field>

                        <Field label="Cover meta (For / Markets / Read time)">
                            <div className="grid gap-2">
                                {editing.meta.map((m, i) => (
                                    <div key={i} className="grid gap-2 sm:grid-cols-[160px_1fr]">
                                        <input
                                            value={m.label}
                                            onChange={(ev) => updateMeta(i, { label: ev.target.value })}
                                            className={inputCls}
                                            placeholder="Label"
                                        />
                                        <input
                                            value={m.value}
                                            onChange={(ev) => updateMeta(i, { value: ev.target.value })}
                                            className={inputCls}
                                            placeholder="Value"
                                        />
                                    </div>
                                ))}
                            </div>
                        </Field>

                        {/* Pages editor */}
                        <div className="mt-2 space-y-8">
                            {editing.pages.map((p, pi) => (
                                <div key={pi} className="border-2 border-ink/10 bg-page p-5" data-testid={`admin-booklets-page-${pi}`}>
                                    <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-coral">
                                        Page {pi + 1}
                                    </div>
                                    <Field label="Heading">
                                        <input
                                            value={p.heading}
                                            onChange={(ev) => updatePage(pi, { heading: ev.target.value })}
                                            className={inputCls}
                                        />
                                    </Field>
                                    <Field label="Intro (1–2 sentences)">
                                        <textarea
                                            rows={3}
                                            value={p.intro}
                                            onChange={(ev) => updatePage(pi, { intro: ev.target.value })}
                                            className={inputCls}
                                        />
                                    </Field>
                                    <Field label="Bullet points">
                                        <div className="space-y-2">
                                            {p.points.map((pt, ptIdx) => (
                                                <div key={ptIdx} className="flex gap-2">
                                                    <input
                                                        value={pt}
                                                        onChange={(ev) => updatePoint(pi, ptIdx, ev.target.value)}
                                                        className={`${inputCls} flex-1`}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => removePoint(pi, ptIdx)}
                                                        className="grid h-10 w-10 place-items-center border-2 border-ink/20 text-ink/55 hover:border-coral hover:text-coral"
                                                        aria-label="Remove bullet"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => addPoint(pi)}
                                                className="inline-flex items-center gap-2 border-2 border-dashed border-ink/25 px-3 py-2 text-xs font-bold uppercase tracking-wider text-ink/70 hover:border-coral hover:text-coral"
                                            >
                                                <Plus className="h-3.5 w-3.5" /> Add bullet
                                            </button>
                                        </div>
                                    </Field>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 grid gap-4 md:grid-cols-3">
                            <Field label="Price (NZD, no $)">
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={editing.price ?? 9}
                                    onChange={(ev) =>
                                        setEditing({ ...editing, price: Number(ev.target.value) || 0 })
                                    }
                                    className={inputCls}
                                    data-testid="admin-booklets-price"
                                />
                            </Field>
                            <Field label="Order (lower = appears first)">
                                <input
                                    type="number"
                                    value={editing.order}
                                    onChange={(ev) =>
                                        setEditing({ ...editing, order: Number(ev.target.value) || 0 })
                                    }
                                    className={inputCls}
                                />
                            </Field>
                            <label className="mt-8 flex items-center gap-3 text-sm text-ink/80">
                                <input
                                    type="checkbox"
                                    checked={editing.is_published}
                                    onChange={(ev) =>
                                        setEditing({ ...editing, is_published: ev.target.checked })
                                    }
                                    className="h-4 w-4"
                                    data-testid="admin-booklets-published"
                                />
                                <span>Published (visible in the store)</span>
                            </label>
                        </div>

                        {/* Upload custom PDF */}
                        {editing.id && (
                            <div className="mt-8 border-2 border-dashed border-ink/20 bg-page p-5">
                                <div className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-coral">
                                    <FileUp className="h-3.5 w-3.5" /> Custom PDF (optional)
                                </div>
                                <p className="mb-4 text-sm text-ink/70">
                                    Upload your own designed PDF (e.g. from Canva). If uploaded, buyers download this file
                                    instead of the auto-generated one.
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="application/pdf"
                                    className="hidden"
                                    onChange={(ev) => uploadPdf(ev.target.files?.[0])}
                                    data-testid="admin-booklets-file-input"
                                />
                                <div className="flex flex-wrap items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        className="inline-flex items-center gap-2 border-2 border-ink bg-white px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-ink hover:border-coral hover:text-coral disabled:opacity-60"
                                        data-testid="admin-booklets-upload"
                                    >
                                        {uploading ? (
                                            <>Uploading… <Loader2 className="h-4 w-4 animate-spin" /></>
                                        ) : (
                                            <><Upload className="h-4 w-4" /> {editing.custom_pdf_path ? "Replace PDF" : "Upload PDF"}</>
                                        )}
                                    </button>
                                    {editing.custom_pdf_path && (
                                        <>
                                            <span className="inline-flex items-center gap-2 border border-coral bg-coral/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-coral">
                                                <BookOpen className="h-3 w-3" /> Custom PDF active
                                            </span>
                                            <button
                                                type="button"
                                                onClick={removePdf}
                                                className="text-[11px] font-bold uppercase tracking-widest text-ink/60 underline underline-offset-4 hover:text-coral"
                                                data-testid="admin-booklets-remove-pdf"
                                            >
                                                Revert to auto-generated
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="mt-8 flex items-center gap-4">
                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex items-center gap-2 border-2 border-coral bg-coral px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white hover:-translate-y-0.5 disabled:opacity-60"
                                data-testid="admin-booklets-save"
                            >
                                {saving ? (
                                    <>Saving… <Loader2 className="h-4 w-4 animate-spin" /></>
                                ) : (
                                    <><Save className="h-4 w-4" /> {editing.id ? "Update" : "Create"}</>
                                )}
                            </button>
                            {editing.id && (
                                <button
                                    type="button"
                                    onClick={() => del(editing.id)}
                                    className="inline-flex items-center gap-2 border-2 border-ink/25 bg-white px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-ink hover:border-coral hover:text-coral"
                                    data-testid="admin-booklets-delete"
                                >
                                    <Trash2 className="h-4 w-4" /> Delete
                                </button>
                            )}
                        </div>
                    </form>
                ) : (
                    <div>
                        {loadingList ? (
                            <div className="text-sm uppercase tracking-widest text-ink/45">Loading…</div>
                        ) : items.length === 0 ? (
                            <div className="border-2 border-dashed border-ink/20 bg-white p-14 text-center">
                                <div className="font-display text-2xl font-black text-ink">No booklets yet.</div>
                                <p className="mt-3 text-sm text-ink/70">Create your first booklet to start selling.</p>
                                <button
                                    onClick={() => setEditing({ ...EMPTY })}
                                    className="mt-6 inline-flex items-center gap-2 border-2 border-coral bg-coral px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white"
                                >
                                    <Plus className="h-4 w-4" /> Create booklet
                                </button>
                            </div>
                        ) : (
                            <div className="divide-y divide-ink/10 border-2 border-ink/15 bg-white">
                                {items.map((r) => (
                                  <div key={r.id}>
                                    <div
                                        className="flex items-center justify-between p-5"
                                        data-testid={`admin-booklets-row-${r.slug}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-coral">
                                                <span>#{r.order}</span>
                                                {!r.is_published && (
                                                    <span className="border border-ink/25 px-2 py-0.5 text-ink/60">Draft</span>
                                                )}
                                                {r.custom_pdf_path && (
                                                    <span className="border border-coral bg-coral/10 px-2 py-0.5 text-coral">
                                                        Custom PDF
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-1 truncate font-display text-lg font-black text-ink">{r.title}</div>
                                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink/60">
                                                <span className="truncate">{r.subtitle}</span>
                                                <span className="border border-ink/20 px-1.5 py-0.5 font-bold uppercase tracking-wider text-coral">
                                                    ${(r.price ?? 9).toFixed(0)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex shrink-0 gap-2">
                                            <a
                                                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/booklets/${r.id}/download`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-2 border-2 border-ink/25 bg-white px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-ink hover:border-coral hover:text-coral"
                                                data-testid={`admin-booklets-download-${r.slug}`}
                                                title="Download the PDF straight from here — no email link needed"
                                            >
                                                <Download className="h-3.5 w-3.5" /> Download
                                            </a>
                                            <button
                                                onClick={() => sendTestEmail(r)}
                                                disabled={testingEmail === r.id}
                                                className="inline-flex items-center gap-2 border-2 border-coral/50 bg-white px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-coral hover:border-coral disabled:opacity-60"
                                                data-testid={`admin-booklets-test-email-${r.slug}`}
                                                title="Send yourself the exact email buyers receive — free, no payment"
                                            >
                                                {testingEmail === r.id ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <Mail className="h-3.5 w-3.5" />
                                                )}
                                                Test email
                                            </button>
                                            <button
                                                onClick={() => setEditing({ ...r })}
                                                className="inline-flex items-center gap-2 border-2 border-ink/25 bg-white px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-ink hover:border-coral hover:text-coral"
                                                data-testid={`admin-booklets-edit-${r.slug}`}
                                            >
                                                <Edit3 className="h-3.5 w-3.5" /> Edit
                                            </button>
                                            <button
                                                onClick={() => del(r.id)}
                                                className="inline-flex items-center gap-2 border-2 border-ink/25 bg-white px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-ink hover:border-coral hover:text-coral"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    {testLinks[r.id] && (
                                        <div className="mt-2 border-t border-ink/10 pt-2" data-testid={`admin-booklets-test-link-${r.slug}`}>
                                            <a
                                                href={testLinks[r.id]}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1.5 text-xs font-bold text-coral underline underline-offset-2 hover:text-ink"
                                            >
                                                <Download className="h-3 w-3" />
                                                Open test PDF — the exact link from the email, no inbox needed
                                            </a>
                                        </div>
                                    )}
                                  </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="mb-5 block">
            <span className="mb-2 block text-[11px] font-bold uppercase tracking-[0.22em] text-ink/60">
                {label}
            </span>
            {children}
        </label>
    );
}
